/**
 * Reports Page - Curriculum-based reporting
 *
 * Merges server scan data with local student names.
 * Privacy: Server only has sheet numbers, names come from local SQLite.
 */

import { useState, useMemo, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Download,
  Users,
  FileText,
  Target,
  BarChart3,
  AlertCircle,
  Database,
} from "lucide-react";
import { useLocalDbStore } from "@/store/localDbStore";
import { classService, type ServerClass } from "@/services/class.service";
import {
  useResultsSummary,
  useWorksheetResults,
  getMasteryLevel,
  calculateStats,
  type MergedScanResult,
} from "@/hooks/useReportData";
import { cn } from "@/lib/utils";

// =============================================================================
// Mastery Badge Component
// =============================================================================

function MasteryBadge({ percentage }: { percentage: number }) {
  const mastery = getMasteryLevel(percentage);

  const variants: Record<string, string> = {
    mastered: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    proficient: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
    developing: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
    needs_support: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  };

  return (
    <Badge className={cn("font-normal", variants[mastery.level])}>
      {mastery.icon} {mastery.label}
    </Badge>
  );
}

// =============================================================================
// Stats Cards Component
// =============================================================================

function StatsCards({
  loading,
  items,
}: {
  loading: boolean;
  items: MergedScanResult[];
}) {
  const stats = useMemo(() => calculateStats(items), [items]);

  if (loading) {
    return (
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <Skeleton className="h-4 w-20" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-12" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Total Scans</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.count}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Average Score</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {stats.avgPercentage !== null ? `${stats.avgPercentage}%` : "--"}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-green-600">Mastered</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.masteredCount}</div>
          <p className="text-xs text-muted-foreground">≥ 85%</p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-red-600">Needs Support</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.needsSupportCount}</div>
          <p className="text-xs text-muted-foreground">&lt; 50%</p>
        </CardContent>
      </Card>
    </div>
  );
}

// =============================================================================
// Results Table Component
// =============================================================================

function ResultsTable({
  items,
  loading,
  showWorksheet = false,
}: {
  items: MergedScanResult[];
  loading: boolean;
  showWorksheet?: boolean;
}) {
  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex items-center gap-4 p-2">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
            <Skeleton className="h-6 w-16" />
          </div>
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="py-12 text-center">
        <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
        <p className="text-muted-foreground">No results found</p>
        <p className="text-sm text-muted-foreground mt-1">
          Scan some worksheets to see results here
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Student</TableHead>
            {showWorksheet && <TableHead className="hidden sm:table-cell">Worksheet</TableHead>}
            <TableHead className="text-right">Score</TableHead>
            <TableHead className="hidden sm:table-cell">Status</TableHead>
            <TableHead className="hidden md:table-cell">Date</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.scanId}>
              <TableCell>
                <div className="flex items-center gap-2">
                  <div
                    className={cn(
                      "h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium",
                      item.isUnknown
                        ? "bg-muted text-muted-foreground"
                        : "bg-primary/10 text-primary"
                    )}
                  >
                    {item.studentName.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className={cn("font-medium", item.isUnknown && "text-muted-foreground")}>
                      {item.studentName}
                    </p>
                    {/* Mobile: show score inline */}
                    <p className="text-xs text-muted-foreground sm:hidden">
                      {item.percentage}% · {getMasteryLevel(item.percentage).label}
                    </p>
                  </div>
                </div>
              </TableCell>
              {showWorksheet && (
                <TableCell className="hidden sm:table-cell text-muted-foreground">
                  --
                </TableCell>
              )}
              <TableCell className="text-right font-medium">
                <span className="hidden sm:inline">{item.totalScore}/{item.maxScore}</span>
                <span className="sm:hidden">{item.percentage}%</span>
              </TableCell>
              <TableCell className="hidden sm:table-cell">
                <MasteryBadge percentage={item.percentage} />
              </TableCell>
              <TableCell className="hidden md:table-cell text-muted-foreground">
                {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "--"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// =============================================================================
// Main Reports Component
// =============================================================================

export default function Reports() {
  const [selectedWorksheetId, setSelectedWorksheetId] = useState<string | null>(null);
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("overview");

  // Server classes state
  const [serverClasses, setServerClasses] = useState<ServerClass[]>([]);
  const [classesLoading, setClassesLoading] = useState(true);

  // Local database state for student name lookups
  const isConnected = useLocalDbStore((s) => s.isConnected);
  const localClasses = useLocalDbStore((s) => s.classes);
  const selectLocalClass = useLocalDbStore((s) => s.selectClass);

  // Fetch server classes
  const fetchServerClasses = useCallback(async (signal: AbortSignal) => {
    try {
      setClassesLoading(true);
      const classes = await classService.getClasses(false, signal);
      if (!signal.aborted) {
        setServerClasses(classes);
      }
    } catch (err) {
      if (!(err instanceof DOMException && err.name === "AbortError")) {
        console.error("Failed to load classes:", err);
      }
    } finally {
      if (!signal.aborted) {
        setClassesLoading(false);
      }
    }
  }, []);

  // Load server classes on mount
  useEffect(() => {
    const controller = new AbortController();
    fetchServerClasses(controller.signal);
    return () => controller.abort();
  }, [fetchServerClasses]);

  // When server class changes, update local DB class selection for student lookup
  useEffect(() => {
    if (!isConnected || !selectedClassId) {
      selectLocalClass(null);
      return;
    }
    // Find local class that matches the server classId
    const matchingLocalClass = localClasses.find(
      (lc) => lc.serverClassId === selectedClassId
    );
    if (matchingLocalClass) {
      selectLocalClass(matchingLocalClass.id);
    } else {
      selectLocalClass(null);
    }
  }, [selectedClassId, localClasses, isConnected, selectLocalClass]);

  // Fetch data - pass classId to filter results
  const { data: summary, loading: summaryLoading, error: summaryError } = useResultsSummary();
  const {
    data: worksheetResults,
    loading: worksheetLoading,
  } = useWorksheetResults(selectedWorksheetId, selectedClassId);

  // Available worksheets from summary
  const worksheets = useMemo(() => {
    if (!summary) return [];
    return summary.items.sort((a, b) => b.scanCount - a.scanCount);
  }, [summary]);

  // Auto-select first worksheet when data loads
  useEffect(() => {
    if (worksheets.length > 0 && !selectedWorksheetId) {
      setSelectedWorksheetId(worksheets[0].worksheetId);
    }
  }, [worksheets, selectedWorksheetId]);

  return (
    <div className="container mx-auto p-4 md:p-6 space-y-6">
      {/* Header - Responsive */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Reports</h1>
          <p className="text-muted-foreground text-sm md:text-base">
            View grading analytics by student and curriculum outcome
          </p>
        </div>
        <Button variant="outline" className="self-start sm:self-auto">
          <Download className="mr-2 h-4 w-4" />
          Export
        </Button>
      </div>

      {/* Database Connection Notice */}
      {!isConnected && (
        <Alert>
          <Database className="h-4 w-4" />
          <AlertTitle>Connect Student Database</AlertTitle>
          <AlertDescription>
            Open your student database on the{" "}
            <a href="/students" className="font-medium underline underline-offset-4">
              Students page
            </a>{" "}
            to see student names in reports. Without it, only sheet numbers will be shown.
          </AlertDescription>
        </Alert>
      )}

      {/* Error State */}
      {summaryError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error loading data</AlertTitle>
          <AlertDescription>{summaryError}</AlertDescription>
        </Alert>
      )}

      {/* Filters - Responsive */}
      <div className="flex flex-col sm:flex-row gap-3">
        {classesLoading ? (
          <Skeleton className="h-10 w-full sm:w-[200px]" />
        ) : serverClasses.length > 0 && (
          <Select
            value={selectedClassId || "all"}
            onValueChange={(v) => setSelectedClassId(v === "all" ? null : v)}
          >
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="Select class" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Classes</SelectItem>
              {serverClasses.map((cls) => (
                <SelectItem key={cls.classId} value={cls.classId}>
                  {cls.name} {cls.grade ? `(Grade ${cls.grade})` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {worksheets.length > 0 && (
          <Select
            value={selectedWorksheetId || ""}
            onValueChange={setSelectedWorksheetId}
          >
            <SelectTrigger className="w-full sm:w-[250px]">
              <SelectValue placeholder="Select worksheet" />
            </SelectTrigger>
            <SelectContent>
              {worksheets.map((ws) => (
                <SelectItem key={ws.worksheetId} value={ws.worksheetId}>
                  {ws.worksheetTitle} ({ws.scanCount})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Tabs - Scrollable on mobile */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
          <TabsList className="inline-flex w-auto min-w-full md:w-full md:grid md:grid-cols-3">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              <span className="hidden sm:inline">Class </span>Overview
            </TabsTrigger>
            <TabsTrigger value="students" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              By Student
            </TabsTrigger>
            <TabsTrigger value="outcomes" className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              By Outcome
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <StatsCards
            loading={worksheetLoading}
            items={worksheetResults?.items || []}
          />

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {worksheetResults?.worksheetTitle || "Worksheet Results"}
              </CardTitle>
              <CardDescription>
                {worksheetResults
                  ? `${worksheetResults.total} completed scans`
                  : "Select a worksheet to view results"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResultsTable
                items={worksheetResults?.items || []}
                loading={worksheetLoading}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* By Student Tab */}
        <TabsContent value="students" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Student Performance
              </CardTitle>
              <CardDescription>
                Individual student results across all worksheets
              </CardDescription>
            </CardHeader>
            <CardContent>
              {summaryLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : !summary || summary.total === 0 ? (
                <div className="py-12 text-center">
                  <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">No student data available</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Scan worksheets to see student performance
                  </p>
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  Student aggregation coming soon
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* By Outcome Tab */}
        <TabsContent value="outcomes" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Curriculum Outcomes
              </CardTitle>
              <CardDescription>
                Performance by learning outcome
              </CardDescription>
            </CardHeader>
            <CardContent>
              {summaryLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : !summary || summary.total === 0 ? (
                <div className="py-12 text-center">
                  <Target className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">No outcome data available</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Scan worksheets linked to curriculum outcomes
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {summary.items.map((ws) => (
                    <div
                      key={ws.worksheetId}
                      className="flex flex-col sm:flex-row sm:items-center justify-between p-3 rounded-lg border hover:bg-muted/50 cursor-pointer gap-2"
                      onClick={() => {
                        setSelectedWorksheetId(ws.worksheetId);
                        setActiveTab("overview");
                      }}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{ws.worksheetTitle}</p>
                        <p className="text-sm text-muted-foreground">
                          {ws.grade && `Grade ${ws.grade} · `}
                          {ws.subject || "General"} · {ws.scanCount} scans
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        {ws.avgPercentage !== null && (
                          <div className="text-right">
                            <p className="font-medium">{ws.avgPercentage}%</p>
                            <p className="text-xs text-muted-foreground">avg</p>
                          </div>
                        )}
                        {ws.avgPercentage !== null && (
                          <MasteryBadge percentage={ws.avgPercentage} />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
